import React, { useMemo } from 'react';

import type { GridColDef, GridColumnGroupingModel, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { MAX_GRID_HEIGHT, isDefaultFeederOriginCode } from './CharterFeederPricesConstants';
import {
  formatCharterFeederDirection,
  isMelFeederOriginCode,
  toFeederBaseField,
  toFeederBufferField,
} from './CharterFeederPricesHelpers';
import {
  CHARTER_FEEDER_DATE_FIELD,
  CHARTER_FEEDER_DIRECTION_FIELD,
  type CharterFeederColumn,
  type CharterFeederSheetRow,
} from './CharterFeederPricesTypes';
import DateCell from './components/DateCell';
import FeederColumnGroupHeader from './components/FeederColumnGroupHeader';

const GRID_SCROLLBAR_GUTTER_WIDTH = 20;

interface UseCharterFeederGridConfigOptions {
  columns: Array<CharterFeederColumn>;
  isViewMode: boolean;
  onRequestClearRow: (row: CharterFeederSheetRow) => void;
  onDeleteFeederColumn: (code: string) => void;
}

interface UseCharterFeederGridConfigResult {
  gridColumns: Array<GridColDef<CharterFeederSheetRow>>;
  columnGroupingModel: GridColumnGroupingModel;
  editableFields: Set<string>;
  gridWidth: number;
  gridSx: Record<string, unknown>;
}

function formatPriceCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

export function useCharterFeederGridConfig({
  columns,
  isViewMode,
  onRequestClearRow,
  onDeleteFeederColumn,
}: UseCharterFeederGridConfigOptions): UseCharterFeederGridConfigResult {
  const editableFields = useMemo(
    () =>
      new Set<string>([
        ...columns.flatMap((column) =>
          isMelFeederOriginCode(column.code)
            ? [toFeederBaseField(column.code)]
            : [toFeederBaseField(column.code), toFeederBufferField(column.code)],
        ),
      ]),
    [columns],
  );

  const editableCellSelector = useMemo(
    () =>
      Array.from(editableFields)
        .map((field) => `& .MuiDataGrid-cell[data-field="${field}"]`)
        .join(', '),
    [editableFields],
  );

  const columnGroupingModel = useMemo<GridColumnGroupingModel>(
    () =>
      columns.map((column) => {
        const children = isMelFeederOriginCode(column.code)
          ? [{ field: toFeederBaseField(column.code) }]
          : [{ field: toFeederBaseField(column.code) }, { field: toFeederBufferField(column.code) }];

        return {
          groupId: `feeder-${column.code}`,
          headerName: column.code,
          renderHeaderGroup: () => (
            <FeederColumnGroupHeader
              code={column.code}
              isFixed={isDefaultFeederOriginCode(column.code)}
              isViewMode={isViewMode}
              onDelete={onDeleteFeederColumn}
            />
          ),
          children,
        };
      }),
    [columns, isViewMode, onDeleteFeederColumn],
  );

  const gridColumns = useMemo<Array<GridColDef<CharterFeederSheetRow>>>(() => {
    const staticColumns: Array<GridColDef<CharterFeederSheetRow>> = [
      {
        field: CHARTER_FEEDER_DATE_FIELD,
        headerName: 'Charter Date',
        width: 170,
        editable: false,
        sortable: true,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<CharterFeederSheetRow>) => (
          <DateCell
            row={params.row}
            rawValue={params.value}
            isViewMode={isViewMode}
            onRequestClear={onRequestClearRow}
          />
        ),
      },
      {
        field: CHARTER_FEEDER_DIRECTION_FIELD,
        headerName: 'Feeder Direction',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        editable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams<CharterFeederSheetRow>) => formatCharterFeederDirection(params.row),
      },
    ];

    const feederColumns: Array<GridColDef<CharterFeederSheetRow>> = columns.flatMap((column) => {
      const baseField = toFeederBaseField(column.code);
      const bufferField = toFeederBufferField(column.code);

      const baseColumn: GridColDef<CharterFeederSheetRow> = {
        field: baseField,
        headerName: 'Base',
        width: 110,
        type: 'number',
        editable: true,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatPriceCellValue(value),
      };

      if (isMelFeederOriginCode(column.code)) {
        return [baseColumn];
      }

      return [
        baseColumn,
        {
          field: bufferField,
          headerName: 'Buffer',
          width: 110,
          type: 'number',
          editable: true,
          align: 'right',
          headerAlign: 'right',
          valueFormatter: (value) => formatPriceCellValue(value),
        },
      ];
    });

    return [...staticColumns, ...feederColumns];
  }, [columns, isViewMode, onRequestClearRow]);

  const gridWidth = useMemo(
    () =>
      gridColumns.reduce((width, column) => width + (column.width ?? column.minWidth ?? 100), 0) +
      GRID_SCROLLBAR_GUTTER_WIDTH,
    [gridColumns],
  );

  const gridSx = useMemo(
    () => ({
      width: gridWidth,
      maxHeight: MAX_GRID_HEIGHT,
      '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'center',
      },
      '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainerContent': {
        justifyContent: 'center',
        width: '100%',
      },
      '& .MuiDataGrid-cell:not([data-field="date"])': {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 'normal',
      },
      '& .MuiDataGrid-cellEmpty': {
        border: 0,
        padding: 0,
      },
      ...(!isViewMode && {
        [editableCellSelector]: {
          outline: '1px solid',
          outlineColor: 'grey.400',
          outlineOffset: -1,
          borderRadius: 1,
          '&:hover': { outlineColor: 'primary.main' },
        },
        '& .MuiDataGrid-cell--editing': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
          boxShadow: 1,
        },
      }),
    }),
    [editableCellSelector, gridWidth, isViewMode],
  );

  return { gridColumns, columnGroupingModel, editableFields, gridWidth, gridSx };
}
